import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSatelliteOverlayStore } from "../store/satelliteOverlayStore";
import { useAIContext } from "../store/aiContextStore";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { importJSON } from "../utils/ImportJSON";

/**
 * Satellite Overlay Risk UI component.
 * Allows users to input satellite overlay parameters and compute risk indices.
 */
export default function SatelliteOverlay() {
  const { t } = useTranslation();
  const store = useSatelliteOverlayStore();
  const [projectSiteID, setProjectSiteID] = useState(store.projectSiteID);
  const [constructionProgress, setConstructionProgress] = useState(store.constructionProgressPercent);
  const [budgetDisbursement, setBudgetDisbursement] = useState(store.budgetDisbursementPercent);
  const [satelliteDrift, setSatelliteDrift] = useState(store.satelliteDriftScore);
  const [landUseMismatch, setLandUseMismatch] = useState(store.landUseMismatch);
  const [procurementAnomaly, setProcurementAnomaly] = useState(store.procurementAnomaly);
  const [crossBorderSuspicion, setCrossBorderSuspicion] = useState(store.crossBorderSuspicion);

  // Set active panel
  useEffect(() => {
    useAIContext.getState().setActivePanel("SatelliteOverlayPanel");
  }, []);

  // Update store when inputs change (avoid store in deps to prevent loops)
  useEffect(() => {
    store.setProjectSiteID(projectSiteID);
    store.setConstructionProgressPercent(constructionProgress);
    store.setBudgetDisbursementPercent(budgetDisbursement);
    store.setSatelliteDriftScore(satelliteDrift);
    store.setLandUseMismatch(landUseMismatch);
    store.setProcurementAnomaly(procurementAnomaly);
    store.setCrossBorderSuspicion(crossBorderSuspicion);
    // Auto-compute on input change
    store.computeOverlayRisk();
  }, [projectSiteID, constructionProgress, budgetDisbursement, satelliteDrift, landUseMismatch, procurementAnomaly, crossBorderSuspicion]);

  const handleCompute = async () => {
    await store.computeOverlayRisk();
  };

  const handleExport = () => {
    store.exportSatelliteOverlay();
  };

  const handleImport = async () => {
    try {
      const data = await importJSON<{
        projectSiteID?: string;
        constructionProgressPercent?: number;
        budgetDisbursementPercent?: number;
        satelliteDriftScore?: number;
        landUseMismatch?: number;
        procurementAnomaly?: number;
        crossBorderSuspicion?: number;
      }>();
      if (data) {
        store.importSatelliteOverlay(data);
      }
    } catch (error) {
      console.error("Import failed:", error);
    }
  };

  // Keyboard shortcuts (after function definitions)
  useKeyboardShortcuts([
    { key: 'e', ctrl: true, action: handleExport, description: 'Export overlay' },
    { key: 'i', ctrl: true, action: handleImport, description: 'Import overlay' },
    { key: 'c', ctrl: true, action: handleCompute, description: 'Compute overlay risk' },
  ]);

  return (
    <div className="space-y-6 font-orbitron bg-black text-neutral-100">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-4 p-6 border border-evat-gold/40 rounded-lg bg-black/50">
          <h2 className="text-xl font-orbitron text-evat-neon border-b border-evat-gold/40 pb-2">
            {t("sat.overlay.inputs") || "Input Parameters"}
          </h2>

          {/* Project Site ID */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("sat.overlay.projectSiteID") || "Project Site ID"}
            </label>
            <input
              type="text"
              value={projectSiteID}
              onChange={(e) => setProjectSiteID(e.target.value)}
              placeholder={t("sat.overlay.projectSiteIDPlaceholder") || "Enter site ID"}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
            />
          </div>

          {/* Construction Progress */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("sat.overlay.constructionProgress") || "Construction Progress"} ({constructionProgress}%)
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={constructionProgress}
              onChange={(e) => setConstructionProgress(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Budget Disbursement */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("sat.overlay.budgetDisbursement") || "Budget Disbursement"} ({budgetDisbursement}%)
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={budgetDisbursement}
              onChange={(e) => setBudgetDisbursement(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Satellite Drift Score */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("sat.overlay.satelliteDriftScore") || "Satellite Drift Score"} ({satelliteDrift})
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={satelliteDrift}
              onChange={(e) => setSatelliteDrift(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Land Use Mismatch */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("sat.overlay.landUseMismatch") || "Land Use Mismatch"} ({landUseMismatch})
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={landUseMismatch}
              onChange={(e) => setLandUseMismatch(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Procurement Anomaly */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("sat.overlay.procurementAnomaly") || "Procurement Anomaly"} ({procurementAnomaly})
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={procurementAnomaly}
              onChange={(e) => setProcurementAnomaly(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Cross-Border Suspicion */}
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              {t("sat.overlay.crossBorderSuspicion") || "Cross-Border Suspicion"} ({crossBorderSuspicion})
            </label>
            <select
              value={crossBorderSuspicion}
              onChange={(e) => setCrossBorderSuspicion(Number(e.target.value))}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
            >
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              onClick={handleCompute}
              className="flex-1 bg-evat-neon text-black px-6 py-3 rounded font-orbitron hover:shadow-neon transition-all"
            >
              {t("sat.overlay.compute") || "Compute Overlay Risk"}
            </button>
            <button
              onClick={handleImport}
              className="flex-1 bg-emerald-700/20 border border-emerald-700 text-emerald-400 px-6 py-3 rounded font-orbitron hover:bg-emerald-700/30 transition-all"
            >
              {t("sat.overlay.import") || "Import Overlay JSON"}
            </button>
            <button
              onClick={handleExport}
              className="flex-1 bg-evat-gold/20 border border-evat-gold text-evat-gold px-6 py-3 rounded font-orbitron hover:bg-evat-gold/30 transition-all"
            >
              {t("sat.overlay.export") || "Export Overlay JSON"}
            </button>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-4 p-6 border border-evat-gold/40 rounded-lg bg-black/50">
          <h2 className="text-xl font-orbitron text-evat-neon border-b border-evat-gold/40 pb-2">
            {t("sat.overlay.results") || "Risk Analysis"}
          </h2>

          {/* Ghost Site Alert */}
          {store.ghostSiteFlag && (
            <div className="p-4 border border-red-500/35 bg-ff0033/20 rounded-lg">
              <div className="text-red-400 font-orbitron text-lg font-bold">
                ⚠️ GHOST CONSTRUCTION ALERT
              </div>
              <div className="text-red-300 text-sm mt-1">
                {t("sat.overlay.ghostSiteWarning") || "Construction progress <10% but budget disbursement >30%. Suspicious pattern detected."}
              </div>
            </div>
          )}

          {/* Overlay Risk Index */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("sat.overlay.overlayRiskIndex") || "Overlay Risk Index"}
            </div>
            <div className={`text-2xl font-orbitron ${
              store.overlayRiskIndex >= 75 ? "text-red-400" :
              store.overlayRiskIndex >= 50 ? "text-orange-400" :
              store.overlayRiskIndex >= 25 ? "text-yellow-400" :
              "text-green-400"
            }`}>
              {store.overlayRiskIndex.toFixed(1)}%
            </div>
          </div>

          {/* Progress Mismatch */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("sat.overlay.progressMismatch") || "Progress Mismatch"}
            </div>
            <div className={`text-lg font-orbitron ${
              store.progressMismatch >= 50 ? "text-red-400" :
              store.progressMismatch >= 25 ? "text-yellow-400" :
              "text-green-400"
            }`}>
              {store.progressMismatch.toFixed(1)}%
            </div>
          </div>

          {/* Land Use Mismatch */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("sat.overlay.landUseMismatch") || "Land Use Mismatch"}
            </div>
            <div className={`text-lg font-orbitron ${
              store.landUseMismatch >= 70 ? "text-red-400" :
              store.landUseMismatch >= 40 ? "text-yellow-400" :
              "text-green-400"
            }`}>
              {store.landUseMismatch.toFixed(1)}%
            </div>
          </div>

          {/* Procurement Anomaly */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("sat.overlay.procurementAnomaly") || "Procurement Anomaly"}
            </div>
            <div className={`text-lg font-orbitron ${
              store.procurementAnomaly >= 70 ? "text-red-400" :
              store.procurementAnomaly >= 40 ? "text-yellow-400" :
              "text-green-400"
            }`}>
              {store.procurementAnomaly.toFixed(1)}%
            </div>
          </div>

          {/* Satellite Drift Score */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("sat.overlay.satelliteDriftScore") || "Satellite Drift Score"}
            </div>
            <div className={`text-lg font-orbitron ${
              store.satelliteDriftScore >= 70 ? "text-red-400" :
              store.satelliteDriftScore >= 40 ? "text-yellow-400" :
              "text-green-400"
            }`}>
              {store.satelliteDriftScore.toFixed(1)}
            </div>
          </div>

          {/* Cross-Border Suspicion */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("sat.overlay.crossBorderSuspicion") || "Cross-Border Suspicion"}
            </div>
            <div className={`text-lg font-orbitron ${
              store.crossBorderSuspicion >= 4 ? "text-red-400" :
              store.crossBorderSuspicion >= 2 ? "text-yellow-400" :
              "text-green-400"
            }`}>
              {store.crossBorderSuspicion.toFixed(1)} / 5.0
            </div>
          </div>

          {/* Ghost Site Flag */}
          <div className="p-4 border border-evat-neon/40 rounded bg-black/30">
            <div className="text-sm text-neutral-400 mb-1">
              {t("sat.overlay.ghostSiteFlag") || "Ghost Site Flag"}
            </div>
            <div className={`text-lg font-orbitron ${
              store.ghostSiteFlag ? "text-red-400" : "text-green-400"
            }`}>
              {store.ghostSiteFlag ? "TRUE" : "FALSE"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

